import { isArray, isEmpty, some } from 'lodash';
import { toSentence } from '.';

export const getProductTypeByResourceClassName = (resourceClassName) => {
  const productTypes = {
    ferry: ['Ship'],
    transfer: ['ScheduledTripResource'],
    accommodation: ['Accommodation', 'TXAChannel', 'ExtraPick'],
    experience: ['Package', 'GenericResource', 'Item'],
  };

  const productTypeGroup = Object.entries(productTypes).find(([, classNames]) =>
    classNames.includes(resourceClassName)
  );

  if (!productTypeGroup) {
    return null;
  }
  return productTypeGroup[0];
};

// Recursively look through a reservation (and it's sub-reservations) to see if
// it contains a productType
export const reservationContainsProductType = (productTypes, reservation) => {
  const searchSpace = productTypes.split(',');
  // type productType = 'ferry'|'accommodation'|'experience'

  // Handle if this Reservation is of productType
  if (
    searchSpace.includes(
      getProductTypeByResourceClassName(reservation?.resource_class_name)
    )
  ) {
    return true;
  }

  if (!isArray(reservation.sub_reservations_attributes)) {
    return false;
  }

  // Check all the sub-reservation attributes
  return some(reservation.sub_reservations_attributes, (subReservatation) =>
    reservationContainsProductType(productTypes, subReservatation)
  );
};

// Pick reservations by product type from current booking
//  Note: This is a shallow look only
// productTypes = 'ferry,accommodation,experience,transfer'
export const getReservationsByProductType = (productTypes, booking) => {
  const searchSpace = productTypes.split(',');

  if (isEmpty(booking)) {
    return [];
  }
  const { reservations_attributes } = booking;
  return reservations_attributes.filter((reservation) =>
    searchSpace.includes(
      getProductTypeByResourceClassName(reservation.resource_class_name)
    )
  );
};

// Pick reservations by either direct product type or recursively by product type of sub-reservations.
// e.g. does a booking contain _any_ accommodation reservations?
export const getDeepReservationsByProductType = (productTypes, booking) => {
  // type productType = 'ferry'|'accommodation'|'experience'
  if (isEmpty(booking)) {
    return [];
  }
  const { reservations_attributes } = booking;
  return reservations_attributes.filter((reservation) =>
    reservationContainsProductType(productTypes, reservation)
  );
};

// productType = 'ferry'|'accommodation'|'experience'
export const isReservationOfType = (reservation, productTypes) => {
  const searchSpace = productTypes.split(',');
  return searchSpace.includes(
    getProductTypeByResourceClassName(reservation.resource_class_name)
  );
};

// resourceClassName = 'Ship'
export const isResourceClassNameEqualTo = (reservation, resourceClassName) => {
  if (reservation.resource_class_name === resourceClassName) return true;
  return false;
};

// Are there any experiences in the booking which includes a particiular product type
// (e.g. ferry, accommodation)
export const someExperienceIncludesProductType = (productType, booking) =>
  some(getReservationsByProductType('experience', booking), (reservation) =>
    reservationContainsProductType(productType, reservation)
  );

export const getUniqueOptions = (subReservationsAttributes) => {
  const subReserervations =
    !!subReservationsAttributes &&
    subReservationsAttributes.map((sub_reservation) =>
      getProductTypeByResourceClassName(sub_reservation.resource_class_name)
    );

  const excludeExp =
    !!subReserervations &&
    subReserervations.filter((sub) => sub !== 'experience');

  return (!!excludeExp && [...new Set(excludeExp)]) || [];
};

export const showUpSell = (reservationType, currentBooking) => {
  // Don't show up-sell if there is no booking
  if (isEmpty(currentBooking)) {
    return false;
  }

  // Don't show up-sell if there is any reservation in the booking for the type
  if (!isEmpty(getReservationsByProductType(reservationType, currentBooking))) {
    return false;
  }

  // Don't show up-sell if any experience includes the type
  if (someExperienceIncludesProductType(reservationType, currentBooking)) {
    return false;
  }

  // Show up-sell only on checkout and overview pages
  if (isEmpty(window.location.pathname.match(/bookings(\/checkout)?\/?$/))) {
    return false;
  }

  return true;
};

export const getReservationNames = (reservations) =>
  toSentence([...new Set(reservations.map((res) => res.selection_name))]);
